/**
 * Protected Route Component
 *
 * Wraps routes that require authentication. Handles:
 * 1. Session validation on initial load (via auth store initialize)
 * 2. Loading state while validating
 * 3. Redirect to login if not authenticated
 * 4. Crisis guard for routes that require active crisis
 *
 * Usage:
 *   <ProtectedRoute>
 *     <YourProtectedComponent />
 *   </ProtectedRoute>
 */

import { type ReactNode, useEffect } from 'react'
import { useAuth } from '../../lib/hooks/useAuth'
import { CrisisGuard } from './CrisisGuard'
import { LoadingScreen } from './LoadingScreen'

interface ProtectedRouteProps {
  children: ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isInitialized, initialize } = useAuth()

  // Initialize auth on mount (validates session with server)
  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [isInitialized, initialize])

  console.debug('[ProtectedRoute] Render:', { isAuthenticated, isLoading, isInitialized })

  // Show loading while initializing or during auth operations
  if (!isInitialized || isLoading) {
    console.debug('[ProtectedRoute] Auth loading/initializing, showing spinner')
    return <LoadingScreen showCard={true} />
  }

  if (!isAuthenticated) {
    console.debug('[ProtectedRoute] Not authenticated, redirecting to /login')
    // Use window.location for auth redirects to ensure clean state reset
    window.location.href = '/login'
    return null
  }

  console.debug('[ProtectedRoute] Authenticated, rendering CrisisGuard')
  return <CrisisGuard>{children}</CrisisGuard>
}
